import React, { useState, useEffect } from 'react';
import axios from '../services/axiosConfig';
import '../styles/perfil-paciente.css';

function PerfilPaciente({ paciente_id, onClose }) {
  const [paciente, setPaciente] = useState(null);
  const [perfil, setPerfil] = useState({});
  const [editando, setEditando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [fotoPreview, setFotoPreview] = useState(null);

  useEffect(() => {
    cargarPerfil();
  }, [paciente_id]);

  const cargarPerfil = async () => {
    try {
      setCargando(true);
      const response = await axios.get(`/api/pacientes/${paciente_id}/perfil`);
      setPaciente(response.data.paciente);
      setPerfil(response.data.perfil);
      
      // Si hay foto en base de datos, mostrarla
      if (response.data.perfil.foto_url) {
        setFotoPreview(response.data.perfil.foto_url);
      }
    } catch (error) {
      console.error('Error cargando perfil:', error);
      setMensaje('Error cargando perfil');
    } finally {
      setCargando(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPerfil(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotoPreview(reader.result);
        setPerfil(prev => ({
          ...prev,
          foto_url: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    
    try {
      setGuardando(true);
      await axios.post(`/api/pacientes/${paciente_id}/perfil`, perfil);
      setMensaje('✅ Perfil actualizado correctamente');
      setEditando(false);
      setTimeout(() => setMensaje(''), 3000);
    } catch (error) {
      console.error('Error guardando perfil:', error);
      setMensaje('❌ Error guardando perfil');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <div className="perfil-loading">Cargando perfil...</div>;
  }

  if (!paciente) {
    return <div className="perfil-error">Paciente no encontrado</div>;
  }

  return (
    <div className="perfil-paciente-container">
      <div className="perfil-header">
        <h2>👤 Perfil de Paciente</h2>
        <button className="btn-cerrar" onClick={onClose}>✕</button>
      </div>

      {mensaje && (
        <div className={`mensaje ${mensaje.includes('✅') ? 'exito' : 'error'}`}>
          {mensaje}
        </div>
      )}

      <div className="perfil-content">
        {/* Sección de Foto */}
        <div className="perfil-foto-section">
          <div className="foto-container">
            {fotoPreview ? (
              <img src={fotoPreview} alt={paciente.nombre} className="foto-perfil" />
            ) : (
              <div className="foto-placeholder">📷</div>
            )}
          </div>
          
          {editando && (
            <div className="foto-upload">
              <label htmlFor="foto-input" className="btn-secondary">
                📸 Cambiar Foto
              </label>
              <input
                id="foto-input"
                type="file"
                accept="image/*"
                onChange={handleFotoChange}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </div>

        {/* Información Básica */}
        <div className="perfil-section">
          <h3>📋 Información Básica</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Nombre</label>
              <p>{paciente.nombre}</p>
            </div>
            <div className="info-item">
              <label>Email</label>
              <p>{paciente.email || 'No especificado'}</p>
            </div>
            <div className="info-item">
              <label>Teléfono</label>
              <p>{paciente.telefono || 'No especificado'}</p>
            </div>
            <div className="info-item">
              <label>Fecha de Nacimiento</label>
              <p>
                {!editando ? (
                  paciente.fecha_nacimiento 
                    ? new Date(paciente.fecha_nacimiento).toLocaleDateString('es-AR')
                    : 'No especificada'
                ) : (
                  <input
                    type="date"
                    name="fecha_nacimiento"
                    value={perfil.fecha_nacimiento || ''}
                    onChange={handleInputChange}
                  />
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Información de Contacto de Emergencia */}
        <div className="perfil-section">
          <div className="section-header">
            <h3>🆘 Contacto de Emergencia</h3>
            {!editando && (
              <button className="btn-edit-icon" onClick={() => setEditando(true)}>✏️</button>
            )}
          </div>
          
          {editando ? (
            <div className="edit-form">
              <div className="form-group">
                <label>Nombre de Contacto</label>
                <input
                  type="text"
                  name="nombre_emergencia"
                  value={perfil.nombre_emergencia || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: María García"
                />
              </div>

              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="tel"
                  name="telefono_emergencia"
                  value={perfil.telefono_emergencia || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: +54911234567"
                />
              </div>

              <div className="form-group">
                <label>Relación</label>
                <select
                  name="relacion_emergencia"
                  value={perfil.relacion_emergencia || ''}
                  onChange={handleInputChange}
                >
                  <option value="">Selecciona relación</option>
                  <option value="familia">Familia</option>
                  <option value="pareja">Pareja</option>
                  <option value="amigo">Amigo</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
          ) : (
            <div className="info-grid">
              <div className="info-item">
                <label>Nombre</label>
                <p>{perfil.nombre_emergencia || 'No especificado'}</p>
              </div>
              <div className="info-item">
                <label>Teléfono</label>
                <p>{perfil.telefono_emergencia || 'No especificado'}</p>
              </div>
              <div className="info-item">
                <label>Relación</label>
                <p>{perfil.relacion_emergencia || 'No especificada'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Información Médica */}
        <div className="perfil-section">
          <h3>⚕️ Información Médica</h3>
          
          {editando ? (
            <div className="edit-form">
              <div className="form-group">
                <label>Alergias</label>
                <textarea
                  name="alergias"
                  value={perfil.alergias || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Penicilina, Mariscos"
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label>Medicamentos Actuales</label>
                <textarea
                  name="medicamentos_actuales"
                  value={perfil.medicamentos_actuales || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Sertraline 50mg, Vitamina D"
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label>Condiciones Médicas</label>
                <textarea
                  name="condiciones_medicas"
                  value={perfil.condiciones_medicas || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Diabetes tipo 2, Hipertensión"
                  rows="2"
                />
              </div>
            </div>
          ) : (
            <div className="info-grid">
              <div className="info-item full">
                <label>🏥 Alergias</label>
                <p>{perfil.alergias || 'No especificadas'}</p>
              </div>
              <div className="info-item full">
                <label>💊 Medicamentos</label>
                <p>{perfil.medicamentos_actuales || 'No especificados'}</p>
              </div>
              <div className="info-item full">
                <label>⚠️ Condiciones Médicas</label>
                <p>{perfil.condiciones_medicas || 'No especificadas'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Notas Privadas del Terapeuta */}
        <div className="perfil-section">
          <h3>📝 Notas Privadas</h3>
          
          {editando ? (
            <textarea
              name="notas_privadas"
              value={perfil.notas_privadas || ''}
              onChange={handleInputChange}
              placeholder="Notas internas sobre el paciente..."
              rows="4"
              className="notas-textarea"
            />
          ) : (
            <div className="notas-display">
              {perfil.notas_privadas || 'Sin notas'}
            </div>
          )}
        </div>

        {/* Estado del Tratamiento */}
        <div className="perfil-section">
          <h3>🏥 Estado del Tratamiento</h3>
          
          {editando ? (
            <select
              name="estado_tratamiento"
              value={perfil.estado_tratamiento || 'en_progreso'}
              onChange={handleInputChange}
              className="estado-select"
            >
              <option value="en_progreso">En Progreso</option>
              <option value="completado">Completado</option>
              <option value="pausado">Pausado</option>
              <option value="sin_iniciar">Sin Iniciar</option>
            </select>
          ) : (
            <div className="estado-badge" style={{
              backgroundColor: perfil.estado_tratamiento === 'en_progreso' ? '#4CAF50' :
                               perfil.estado_tratamiento === 'completado' ? '#2196F3' :
                               perfil.estado_tratamiento === 'pausado' ? '#FF9800' : '#999'
            }}>
              {perfil.estado_tratamiento === 'en_progreso' && '🔄 En Progreso'}
              {perfil.estado_tratamiento === 'completado' && '✅ Completado'}
              {perfil.estado_tratamiento === 'pausado' && '⏸️ Pausado'}
              {perfil.estado_tratamiento === 'sin_iniciar' && '⭕ Sin Iniciar'}
            </div>
          )}
        </div>

        {/* Botones de Acción */}
        <div className="perfil-acciones">
          {editando ? (
            <>
              <button 
                className="btn-primary"
                onClick={handleGuardar}
                disabled={guardando}
              >
                {guardando ? '💾 Guardando...' : '💾 Guardar Cambios'}
              </button>
              <button 
                className="btn-secondary"
                onClick={() => {
                  setEditando(false);
                  cargarPerfil();
                }}
                disabled={guardando}
              >
                ✕ Cancelar
              </button>
            </>
          ) : (
            <>
              <button 
                className="btn-primary"
                onClick={() => setEditando(true)}
              >
                ✏️ Editar Perfil
              </button>
              <button 
                className="btn-secondary"
                onClick={onClose}
              >
                Cerrar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PerfilPaciente;
